import React,{useEffect,useState} from 'react';
import {createPortal} from 'react-dom';
import {doc,getDoc} from 'firebase/firestore';
import {db} from '../../Security/firebase-config';
import {useAuth} from '../../context/AuthContext';
import {hasTeamFeature,resolveTeamEntitlements} from '../../utils/entitlements';
import {resolveSetupEvent,setupKey} from '../../utils/eventSetup';
import {memberIds} from '../homework/homeworkModel';
import CalendarView from './CalendarView';

// Shared event editing and recurrence/save flow for Home and its dialogs.
// This component has no dependency on a standalone page or calendar route.
export default function CalendarEventEditor({event,create,timeZone,onClose,actionPosition,openActions=true}) {
  const {userData}=useAuth(),teamId=event?.__teamId||create?.teamId,personal=(event?.calendar||create?.calendar||'personal')==='personal';
  const eventKey=event?setupKey(event,'map-1',personal?'personal':'team'):null;
  const identity=JSON.stringify([userData?.uid,personal,teamId,eventKey,create]);
  const [state,setState]=useState(null);
  useEffect(()=>{
    const overflow=document.body.style.overflow,previous=document.activeElement;
    document.body.style.overflow='hidden';
    return()=>{document.body.style.overflow=overflow;if(previous?.isConnected)previous.focus();};
  },[]);
  useEffect(()=>{
    let alive=true;
    async function load() {
      if(!userData?.uid)throw new Error('Sign in to edit this event.');
      if(personal) {
        if(create){setState({identity,create});return;}
        const user=await getDoc(doc(db,'users',userData.uid));
        if(!alive)return;
        const current=user.exists()?resolveSetupEvent(user.data().events,eventKey):null;
        if(!current)throw new Error('This event is no longer available.');
        setState({identity,event:{...current,calendar:'personal'}});
        return;
      }
      const [team,subscription,entitlements]=await Promise.all([getDoc(doc(db,'teams',teamId)),getDoc(doc(db,'teams',teamId,'billingV2','subscription')),
      getDoc(doc(db,'teams',teamId,'accessV2','entitlements')).catch(()=>null)]);
      if(!alive)return;
      if(!team.exists()||!memberIds(team.data()).includes(userData?.uid))throw new Error('You are no longer a member of this team.');
      const access=resolveTeamEntitlements(entitlements?.exists()?entitlements.data():subscription.exists()?subscription.data():null);
      if(!hasTeamFeature(access,'teamCalendar'))throw new Error('Editing calendar events requires Basic or higher for this team.');
      if(create){setState({identity,create});return;}
      const current=resolveSetupEvent(team.data().events,eventKey);
      if(!current)throw new Error('This event is no longer available.');
      setState({identity,event:{...current,calendar:current.calendar||'team',__teamId:teamId}});
    }
    load().catch(error=>{if(alive)setState({identity,error:error.message||'Could not open the event editor.'});});
    return()=>{alive=false;};
  },[identity,teamId,eventKey,personal,userData?.uid,create]);
  // Render the known event immediately. Fresh access/event validation runs in
  // parallel; CalendarView blocks mutations until it has completed.
  const data=state?.identity===identity?state:{identity,event,create};
  return createPortal(<div className="overview-event-editor">
    {data?.create?<CalendarView editorOnly editorValidationPending={state?.identity!==identity} addDraft={data.create} teamIdOverride={teamId} initialTimeZone={timeZone} onEditorClose={onClose}/>:data?.event?<CalendarView editorOnly editorValidationPending={state?.identity!==identity} openActions={openActions} actionPosition={actionPosition} editEvent={data.event} teamIdOverride={teamId} initialTimeZone={timeZone} onEditorClose={onClose}/>
      :<div className="overview-event-editor__loading"><p role={data?.error?'alert':'status'}>{data?.error||'Loading event editor…'}</p><button type="button" onClick={onClose}>Close event editor</button></div>}
  </div>,document.body);
}
